/**
 * @file ChefProductionSummary.tsx
 * @description Production metrics, speed turnaround KPIs, prepared dishes leaderboard,
 * real date/time completed order audit stream, and prior years annual rollups.
 */

import React from 'react';
import { ChefHat, Clock, AlertTriangle, Flame, Award, Banknote, Calendar, CheckCircle2 } from 'lucide-react';
import type { ChefProductionStats, StatsTimeframe } from '../../../types/stats.types';

interface ChefProductionSummaryProps {
  stats: ChefProductionStats | null;
  timeframe: StatsTimeframe;
  onTimeframeChange: (tf: StatsTimeframe) => void;
  isLoading: boolean;
}

const TIMEFRAME_OPTIONS: { id: StatsTimeframe; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: '6months', label: '6 Months' },
  { id: 'year', label: 'Year' },
];

export const ChefProductionSummary: React.FC<ChefProductionSummaryProps> = ({
  stats,
  timeframe,
  onTimeframeChange,
  isLoading,
}) => {
  return (
    <div className="space-y-5">
      {/* Timeframe Switcher */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
        <div>
          <h3 className="text-sm font-black text-zinc-200 uppercase tracking-wider">
            Kitchen Production Performance
          </h3>
          <p className="text-[11px] text-zinc-500">
            Real-time turnaround metrics across active and historical service windows
          </p>
        </div>
        <div className="flex items-center gap-1 bg-zinc-900 p-1 rounded-xl border border-zinc-800 self-start sm:self-auto overflow-x-auto max-w-full">
          {TIMEFRAME_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => onTimeframeChange(opt.id)}
              className={`px-3 py-1.5 min-h-[32px] rounded-lg text-xs font-bold whitespace-nowrap transition-colors cursor-pointer ${
                timeframe === opt.id
                  ? 'bg-amber-500 text-black font-black shadow-sm'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/60'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-zinc-500 text-sm animate-pulse">
          Calculating kitchen production statistics...
        </div>
      ) : !stats ? (
        <div className="text-center py-12 text-zinc-500 text-sm">
          No statistics available.
        </div>
      ) : (
        <>
          {/* Main KPI Cards Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stats.permissions.show_production_counts && (
              <div className="p-3.5 rounded-xl bg-zinc-900/80 border border-zinc-800">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <ChefHat className="w-4 h-4 text-amber-400" />
                  <span>Orders Plated</span>
                </div>
                <p className="text-2xl font-mono font-black text-white">{stats.orders_prepared}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">{stats.items_prepared} items cooked</p>
              </div>
            )}

            {stats.permissions.show_prep_times && (
              <div className="p-3.5 rounded-xl bg-zinc-900/80 border border-zinc-800">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span>Avg Prep Time</span>
                </div>
                <p className="text-2xl font-mono font-black text-white">
                  {stats.avg_prep_minutes} <span className="text-sm font-normal text-zinc-400">min</span>
                </p>
                <p className="text-[11px] text-emerald-400 mt-0.5">Target: 15 min</p>
              </div>
            )}

            {stats.permissions.show_delayed_counts && (
              <div className="p-3.5 rounded-xl bg-zinc-900/80 border border-zinc-800">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <AlertTriangle className="w-4 h-4 text-rose-400" />
                  <span>Delayed Orders</span>
                </div>
                <p className="text-2xl font-mono font-black text-rose-300">{stats.delayed_orders}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">&gt; 25 min turnaround</p>
              </div>
            )}

            {stats.permissions.show_rush_counts && (
              <div className="p-3.5 rounded-xl bg-zinc-900/80 border border-zinc-800">
                <div className="flex items-center gap-2 text-zinc-400 text-xs mb-1">
                  <Flame className="w-4 h-4 text-orange-400" />
                  <span>Rush Tickets</span>
                </div>
                <p className="text-2xl font-mono font-black text-orange-300">{stats.rush_orders}</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">Expedited</p>
              </div>
            )}
          </div>

          {/* Optional items monetary throughput if permitted by venue owner */}
          {stats.permissions.show_items_value && stats.items_value !== null && (
            <div className="p-3.5 rounded-xl bg-emerald-950/20 border border-emerald-800/40 flex items-center justify-between">
              <div className="flex items-center gap-2 text-emerald-400 text-xs font-bold uppercase tracking-wider">
                <Banknote className="w-4 h-4" />
                <span>Total Value Cooked</span>
              </div>
              <p className="text-lg font-mono font-black text-emerald-300">
                {stats.items_value.toLocaleString()} MAD
              </p>
            </div>
          )}

          {/* Top Prepared Dishes Leaderboard */}
          {stats.permissions.show_top_dishes && stats.top_dishes.length > 0 && (
            <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800">
              <div className="flex items-center gap-2 text-zinc-300 text-xs font-bold uppercase tracking-wider mb-3">
                <Award className="w-4 h-4 text-amber-400" />
                <span>Most Prepared Dishes ({timeframe.toUpperCase()})</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {stats.top_dishes.map((dish, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-zinc-950/40 border border-zinc-800/60 text-xs">
                    <div className="flex items-center gap-2 min-w-0 pr-2">
                      <span className="font-mono text-zinc-500 w-4">{idx + 1}.</span>
                      <span className="font-semibold text-zinc-200 truncate">{dish.name}</span>
                    </div>
                    <span className="font-mono font-bold text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20 shrink-0">
                      {dish.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Real Date & Time Recent Completed Orders */}
          {stats.recent_orders && stats.recent_orders.length > 0 && (
            <div className="p-4 rounded-xl bg-zinc-900/60 border border-zinc-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-zinc-300 text-xs font-bold uppercase tracking-wider">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Recent Plated Orders (Live Timestamp Log)</span>
                </div>
                <span className="text-[11px] text-zinc-500 font-mono">Last 8 tickets</span>
              </div>
              <div className="space-y-1.5">
                {stats.recent_orders.map(order => {
                  const completedDate = order.completed_at ? new Date(order.completed_at) : new Date(order.created_at);
                  const formattedTime = completedDate.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: stats.timezone || undefined,
                  });
                  const formattedDate = completedDate.toLocaleDateString([], {
                    month: 'short',
                    day: 'numeric',
                    timeZone: stats.timezone || undefined,
                  });

                  return (
                    <div
                      key={order.order_id}
                      className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-950/60 border border-zinc-800/80 text-xs"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-mono font-bold text-white shrink-0">{order.order_number}</span>
                        {order.table_id && (
                          <span className="text-zinc-400 truncate">Table {order.table_id}</span>
                        )}
                        <span className="text-zinc-500 hidden sm:inline">{order.items_count} items</span>
                        {order.priority === 'rush' && (
                          <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400 text-[10px] font-bold uppercase">
                            Rush
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="font-mono text-zinc-400 text-[11px]">
                          {formattedDate} {formattedTime}
                        </span>
                        <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded ${
                          order.prep_minutes > 25
                            ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                            : 'bg-zinc-800 text-emerald-400'
                        }`}>
                          {order.prep_minutes}m
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Prior Years Annual Totals Section */}
          {stats.prior_years_totals && stats.prior_years_totals.length > 0 && (
            <div className="p-4 rounded-xl bg-zinc-900/40 border border-zinc-800/80">
              <div className="flex items-center gap-2 text-zinc-300 text-xs font-bold uppercase tracking-wider mb-2">
                <Calendar className="w-4 h-4 text-blue-400" />
                <span>Prior Years Performance (Annual Totals)</span>
              </div>
              <p className="text-[11px] text-zinc-500 mb-3">
                Historical annual production rollups for previous years.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-500">
                      <th className="pb-2 font-bold">Year</th>
                      <th className="pb-2 font-bold">Orders Done</th>
                      <th className="pb-2 font-bold">Items Cooked</th>
                      <th className="pb-2 font-bold">Avg Prep</th>
                      {stats.permissions.show_items_value && (
                        <th className="pb-2 font-bold text-right">Total Value</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/50">
                    {stats.prior_years_totals.map(pt => (
                      <tr key={pt.year} className="text-zinc-300">
                        <td className="py-2.5 font-mono font-bold text-white">{pt.year}</td>
                        <td className="py-2.5 font-mono">{pt.orders_prepared.toLocaleString()}</td>
                        <td className="py-2.5 font-mono">{pt.items_prepared.toLocaleString()}</td>
                        <td className="py-2.5 font-mono text-emerald-400">{pt.avg_prep_minutes} min</td>
                        {stats.permissions.show_items_value && (
                          <td className="py-2.5 font-mono text-right text-emerald-300">
                            {pt.total_value.toLocaleString()} MAD
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

